import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import contentService from "../../services/content";
import { generatePublicUrl } from "../../utils/generatePublicUrl";
import queryDefault from "../../utils/queryDefault";

export default function ContentEdit() {
  const { id } = useParams();
  const notification = useNotification();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);

  const [banner, setBanner] = useState<FileList | null>(null);

  const [localBannerPreview, setLocalBannerPreview] = useState<string | null>(
    null
  );

  // Validation schema
  const schema = yup.object().shape({
    type: yup.string().required("Type is a required field"),
  });

  const form = useForm({
    defaultValues: {
      type: "",
      content: "",
      align: "",
      order: 0,
      username: user.username,
    },
    resolver: yupResolver(schema),
  });

  const updateContent = useMutation("update_content", contentService.update, {
    onSuccess: () => {
      navigate(
        `/app/cms/blog/detail/${get(currentContent, "data.data.blog_id")}`
      );

      notification.onOpen({
        message: "Blog was successfully updated!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const currentContent = useQuery("current-content", () =>
    contentService.getContentById(Number(id))
  );

  useEffect(() => {
    if (currentContent.isFetched) {
      form.setValue("type", get(currentContent, "data.data.type"));
      form.setValue("align", get(currentContent, "data.data.align"));
      form.setValue("content", get(currentContent, "data.data.content"));
      form.setValue("order", get(currentContent, "data.data.order"));
      const bannerUrl = get(currentContent, "data.data.img_path");
      setLocalBannerPreview(generatePublicUrl(bannerUrl));
      form.setValue("username", user.username);
    }
  }, [id, currentContent, form, user.username]);

  const onBannerChange = (files: FileList | null) => {
    const file = files ? files[0] : null;
    if (file) {
      setLocalBannerPreview(URL.createObjectURL(file));
      setBanner(files);
    }
  };

  const onSubmit = (values: any) => {
    const formData = new FormData();
    formData.append("id", id || "");
    formData.append("type", values.type);
    formData.append("content", values.content);
    formData.append("align", values.align);
    formData.append("order", values.order);
    formData.append("username", user.username);
    if (banner) {
      formData.append("file", banner[0]); // Append file to FormData
    }

    updateContent.mutate(formData);
  };

  const type = form.watch("type");

  const typeList = useQuery("content-type-list", () =>
    contentService.getContentType()
  );

  return (
    <MainLayout>
      <Breadcrumb
        label="Edit Content"
        breadcrumbs={[
          { label: "Blog Management", href: "/app/cms/blog" },
          {
            label: "Blog Detail",
            href: `/app/cms/blog/detail/${get(
              currentContent,
              "data.data.blog_id"
            )}`,
          },
          {
            label: "Edit Content",
            href: `/app/cms/blog/content/edit/${id}`,
          },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="blog"
        requiredPermissions={{ is_update: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Type</FormLabel>
                <Controller
                  name="type"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        {get(typeList, ["data", "data"], []).map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.type} key={item?.type}>
                              {item?.desc}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
              {type === "img" ? (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel>Image</FormLabel>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onBannerChange(e.target.files)}
                    style={{ marginTop: 8 }}
                  />
                </FormControl>
              ) : (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel>Content</FormLabel>
                  <Controller
                    name="content"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>
              )}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Align</FormLabel>
                <Controller
                  name="align"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value="left" key="left">
                          Align Left
                        </MenuItem>
                        <MenuItem value="right" key="right">
                          Align Right
                        </MenuItem>
                        <MenuItem value="center" key="center">
                          Align Center
                        </MenuItem>
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Order</FormLabel>
                <Controller
                  name="order"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
          {type === "img" && localBannerPreview && (
            <Stack mt={2}>
              <Typography>Image Preview</Typography>
              <img
                width={300}
                style={{
                  marginTop: 20,
                }}
                src={localBannerPreview}
                alt="Banner Preview"
              />
            </Stack>
          )}
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={updateContent.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
